import { useCallback, useState, useRef, createRef } from 'react';
import { useDropzone } from 'react-dropzone';
import STLViewer from './STLViewer';
import { PrintForm, PrintFormRef } from './PrintCard/Form/PrintForm';
import { useCartStore } from '../stores/cartStore';
import { motion } from 'framer-motion';
import { FileCard, CartItem } from '../types/cart';
import { validateSTL } from '../utils/stlValidator';
import { toast } from 'react-toastify';
import { useTheme } from '../hooks/useTheme'

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_ZIP_SIZE = 50 * 1024 * 1024; // 50MB

const FileUpload = () => {
  const [files, setFiles] = useState<FileCard[]>([]);
  const [prices, setPrices] = useState<{ [key: number]: number }>({});
  const [volumes, setVolumes] = useState<{ [key: number]: number }>({});
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const cartStore = useCartStore()
  const [formValidation, setFormValidation] = useState<{ [key: number]: boolean }>({});
  const theme = useTheme(state => state.getThemeStyles())
  
  // Adicionar ref para cada formulário
  const printFormRefs = useRef<{ [key: number]: React.RefObject<PrintFormRef> }>({});

  // Função para obter ou criar ref para um timestamp específico
  const getFormRef = (timestamp: number) => {
    if (!printFormRefs.current[timestamp]) {
      printFormRefs.current[timestamp] = createRef<PrintFormRef>();
    }
    return printFormRefs.current[timestamp];
  };

  const handleVolumeCalculated = useCallback((volume: number, timestamp: number) => {
    setVolumes(prev => {
      if (prev[timestamp] === volume) return prev;
      return {
        ...prev,
        [timestamp]: volume
      };
    });
  }, []);

  const validateFile = (file: File): boolean => {
    if (!file.name.toLowerCase().endsWith('.stl')) {
      setUploadErrors(prev => [...prev, `${file.name} não é um arquivo STL válido`]);
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadErrors(prev => [...prev, `${file.name} excede o tamanho máximo de 50MB`]);
      return false;
    }

    // Calcula tamanho total incluindo arquivos existentes
    const totalSize = files.reduce((acc, f) => acc + f.file.size, 0) + file.size;
    if (totalSize > MAX_ZIP_SIZE) {
      setUploadErrors(prev => [...prev, `Tamanho total dos arquivos excede 50MB`]);
      return false;
    }

    return true;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploadErrors([]);
    const currentTotalSize = files.reduce((acc, f) => acc + f.file.size, 0);
    
    // Array para armazenar arquivos válidos
    const validFiles: File[] = [];
    
    // Validar cada arquivo
    for (const file of acceptedFiles) {
      // Validação de extensão
      if (!file.name.toLowerCase().endsWith('.stl')) {
        setUploadErrors(prev => [...prev, `${file.name} não é um arquivo STL`]);
        continue;
      }

      // Validação de tamanho
      if (file.size > MAX_FILE_SIZE) {
        setUploadErrors(prev => [...prev, `${file.name} excede 50MB`]);
        continue;
      }

      // Validação de tamanho total
      if (currentTotalSize + file.size > MAX_ZIP_SIZE) {
        setUploadErrors(prev => [...prev, `Tamanho total excede 50MB`]);
        continue;
      }

      // Validação do conteúdo STL
      const validation = await validateSTL(file);
      if (!validation.isValid) {
        setUploadErrors(prev => [...prev, `${file.name}: ${validation.error}`]);
        continue;
      }

      validFiles.push(file);
    }

    // Criar FileCards apenas para arquivos válidos
    const newFiles: FileCard[] = validFiles.map(file => ({
      file,
      timestamp: Date.now() + Math.random(),
      preview: URL.createObjectURL(file),
      status: 'loading'
    }));

    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [files]);

  const removeFile = (timestamp: number) => {
    setFiles(prev => prev.filter(file => file.timestamp !== timestamp));
    setPrices(prev => {
      const newPrices = { ...prev };
      delete newPrices[timestamp];
      return newPrices;
    });
    setVolumes(prev => {
      const newVolumes = { ...prev };
      delete newVolumes[timestamp];
      return newVolumes;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'model/stl': ['.stl'],
    },
  });

  const sortedFiles = [...files].sort((a, b) => a.timestamp - b.timestamp);

  const handlePriceCalculation = useCallback((total: number, timestamp: number) => {
    setPrices(prev => {
      if (prev[timestamp] === total) return prev;
      return {
        ...prev,
        [timestamp]: total || 0
      };
    });
  }, []);

  const handleGenerateQuote = useCallback((fileCard: FileCard, index: number) => {
    const formRef = printFormRefs.current[fileCard.timestamp];
    const services = formRef?.current?.prepareServices();
    
    if (!services) {
      toast.error('Erro ao preparar serviços');
      return;
    }

    // Verifica se já existe um item com o mesmo arquivo e serviços
    const existingItem = cartStore.items.find(item => {
      const sameFile = item.fileName === fileCard.file.name;
      const sameServices = 
        item.services.technology === services.technology &&
        item.services.printer === services.printer &&
        item.services.material === services.material &&
        item.services.color === services.color &&
        item.services.infill === services.infill &&
        item.services.quality === services.quality &&
        item.services.additionalService === services.additionalService;
      
      return sameFile && sameServices;
    });

    if (existingItem) {
      // Se existir, incrementa a quantidade
      cartStore.incrementQuantity(existingItem.id);
      toast.success('Quantidade atualizada no carrinho');
      return;
    }

    // Se não existir, cria um novo item
    const newItem: CartItem = {
      id: crypto.randomUUID(),
      cardIndex: index,
      timestamp: fileCard.timestamp,
      fileName: fileCard.file.name,
      file: fileCard.file,
      quantity: 1,
      price: prices[fileCard.timestamp] || 0,
      services
    };

    cartStore.addItem(newItem);
    toast.success('Item adicionado ao carrinho');
  }, [cartStore, prices]);

  const handleFormValidation = useCallback((isValid: boolean, timestamp: number) => {
    setFormValidation(prev => {
      if (prev[timestamp] === isValid) return prev;
      return { ...prev, [timestamp]: isValid };
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className={theme.components.upload.wrapper}>
        {/* Área de Drop */}
        <div 
          {...getRootProps()} 
          className={`${theme.components.upload.dropzone.wrapper} ${
            isDragActive ? theme.components.upload.dropzone.active : ''
          }`}
        >
          <input {...getInputProps()} className={theme.components.upload.dropzone.input} />
          <div className={theme.components.upload.dropzone.background} />
          
          <div className={theme.components.upload.dropzone.content.wrapper}>
            <svg 
              className={theme.components.upload.dropzone.content.icon}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>

            <div className="text-center">
              <p className={theme.components.upload.dropzone.content.text.primary}>
                Arraste e solte arquivos STL aqui, ou clique para selecionar
              </p>
              <p className={theme.components.upload.dropzone.content.text.secondary}>
                Tamanho máximo: 50MB
              </p>
            </div>
          </div>
        </div>

        {/* Mensagens de erro */}
        {uploadErrors.length > 0 && (
          <div className={theme.components.upload.error.wrapper}>
            <ul className={theme.components.upload.error.list}>
              {uploadErrors.map((error, index) => (
                <li key={index} className={theme.components.upload.error.message}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Lista de Arquivos */}
        <div className="space-y-4">
          {sortedFiles.map((fileCard, index) => (
            <div 
              key={fileCard.timestamp}
              data-file-id={fileCard.timestamp}
              className="relative bg-gray-900/50 rounded-lg border-[3px] border-cyan-500/10"
            >
              {/* Title Bar */}
              <div className="relative flex items-center h-10 bg-gray-900/95 px-2 border-b border-cyan-500/10">
                {/* Background fixo */}
                <div className="absolute inset-0
                  bg-[radial-gradient(circle_at_50%_-20%,rgba(34,211,238,0.1),transparent_70%)]" />
                
                {/* Número do Arquivo - Ajustado posicionamento */}
                <div className="relative -ml-6 -mt-2 z-50">
                  {/* Hexágono de Fundo */}
                  <div className="absolute inset-0 bg-cyan-400/5 scale-[1.35]
                    transition-colors duration-300 clip-path-hexagon" />
                  
                  {/* Anel Externo Pulsante */}
                  <div className="absolute inset-0 border border-cyan-400/20 scale-[1.2]
                    animate-pulse-slow clip-path-hexagon" />
                  
                  {/* Container Principal */}
                  <div className="relative flex h-9 w-9 items-center justify-center 
                    clip-path-hexagon border border-cyan-500/20 bg-gray-900/80
                    before:absolute before:inset-0 before:bg-gradient-to-br 
                    before:from-cyan-400/5 before:to-transparent">
                    <span className="text-lg font-medium text-cyan-400 relative z-10
                      drop-shadow-[0_0_3px_rgba(34,211,238,0.5)]">
                      {index + 1}
                    </span>
                  </div>
                </div>

                {/* Nome do Arquivo */}
                <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-4">
                  <span className="truncate text-sm font-medium text-cyan-100/90">
                    {fileCard.file.name}
                  </span>
                  <span className="text-xs text-cyan-400/50">
                    {(fileCard.file.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </div>

                {/* Botão Remover - Ajustado posicionamento */}
                <button
                  onClick={() => removeFile(fileCard.timestamp)}
                  className="relative -mr-6 -mt-2 h-8 w-8 flex items-center justify-center rounded-full
                    border border-cyan-500/20 bg-gray-900/80 hover:border-red-500/30
                    transition-all duration-300 z-30"
                  aria-label="Remover arquivo"
                >
                  <svg className="w-5 h-5 text-cyan-400/70 hover:text-red-400 
                    transition-all duration-300 hover:rotate-90" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Container do conteúdo com overflow */}
              <div className="overflow-visible relative">
                <div className="p-4 grid grid-cols-3 gap-4">
                  {/* Coluna da esquerda */}
                  <div className="col-span-1 relative">
                    {/* Visualizador 3D */}
                    <div className="mb-4">
                      <STLViewer 
                        file={fileCard.file}
                        onVolumeCalculated={(volume) => handleVolumeCalculated(volume, fileCard.timestamp)}
                      />
                    </div>

                    {/* Valor fixo na parte inferior */}
                    <div className="absolute bottom-0 left-0 right-0">
                      {/* Separador com design do header */}
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 1 }}
                        className="h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent 
                          mx-auto mb-4"
                      />
                      
                      {/* Valor centralizado */}
                      <div className="text-center pb-4">
                        <span className="text-cyan-400/70 text-lg">
                          Valor:
                        </span>
                        <span className="block mt-1 text-3xl font-semibold text-transparent bg-clip-text 
                          bg-gradient-to-r from-orange-400 to-orange-500">
                          R$ {(prices[fileCard.timestamp] || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-2 border border-blue-500/30 rounded-lg p-4 relative flex flex-col">
                    <div className={theme.components.input.variants.form.wrapper}>
                      <h3 className={`${theme.components.input.variants.form.label.base} text-lg`}>
                        <span>Especificações</span>
                        <div className="h-[1px] flex-grow bg-gradient-to-r from-blue-500/20 to-transparent"></div>
                      </h3>
                      
                      <div className="flex-grow">
                        <PrintForm
                          ref={getFormRef(fileCard.timestamp)}
                          volumeCm3={volumes[fileCard.timestamp] || 0}
                          onCalculatePrice={(price) => handlePriceCalculation(price, fileCard.timestamp)}
                          onValidChange={(isValid) => handleFormValidation(isValid, fileCard.timestamp)}
                          cardIndex={index + 1}
                        />
                      </div>

                      {uploadErrors.length > 0 && (
                        <div className={theme.components.input.variants.upload.error.wrapper}>
                          <ul className={theme.components.input.variants.upload.error.list}>
                            {uploadErrors.map((error, index) => (
                              <li key={index} className={theme.components.input.variants.upload.error.message}>
                                {error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Footer do cartão */}
                    <div className="mt-4 pt-4 border-t border-blue-500/20">
                      <div className="flex justify-center">
                        <button 
                          className={`relative group overflow-hidden px-8 py-3.5 rounded-lg
                            border transition-all duration-300
                            ${!formValidation[fileCard.timestamp] 
                              ? 'bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-600/10 to-cyan-500/10 hover:from-blue-600/20 hover:to-cyan-500/20 border-cyan-400/30'
                            }`}
                          onClick={() => handleGenerateQuote(fileCard, index)}
                          disabled={!formValidation[fileCard.timestamp]}
                        >
                          <div className="relative flex items-center justify-center gap-3">
                            <span className={`text-sm font-medium tracking-wider uppercase
                              ${!formValidation[fileCard.timestamp] 
                                ? 'text-gray-600'
                                : 'text-cyan-400 group-hover:text-cyan-300 transition-colors'
                              }`}>
                              Adicionar ao Carrinho
                            </span>
                            <svg 
                              className={`w-4 h-4 transition-all duration-300
                                ${!formValidation[fileCard.timestamp]
                                  ? 'text-gray-600'
                                  : 'text-cyan-400 group-hover:text-cyan-300 group-hover:translate-x-0.5'
                                }`}
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                                d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FileUpload; 